import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import IProps from './batch-add-animation-interface';
import styles from './batch_add_animation.less';
import {
    ADD_ANIMATION_STYLE_TITLE,
    ADD_ANIMATION_TYPE_LIST,
    ADD_ANIMATION_TYPE_TITLE,
    ADD_ANIMATION_TYPE_VALUE,
    ADD_SLIDE_RANGE_TITLE,
    ADD_SLIDE_RANGE_TYPE_LIST,
    ADD_SLIDE_RANGE_TYPE_VALUE,
    BATCH_ANIMATION_TITLE,
} from './constants';
import Select from '@tencent/dui/lib/components/Select';
import Radio from '@tencent/dui/lib/components/Radio';
import { AnimType, ISlideDataModel } from '@tencent/slide-sdk-model';
import { IAnimationEditorService } from '@src/slide/application/service/animation-editor-manager/core/interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ITag } from '@tencent/dui/lib/components/Select/interface';
import {
    EventCodeType,
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
const BatchAddAnimation: React.FC<IProps> = (props: IProps) => {
    const {
        onClose,
        entrList,
        getAnimationEditorService,
        slideDataList,
        selectedSlideList,
        currentSlide,
        dialogVisible,
        getEditorAnimPreviewManager,
        slideModelList,
        currentSlideIndex,
    } = props;
    // 初始化页面范围值
    const [addSlideRangeValue, setAddSlideRangeValue] = useState<string>(ADD_SLIDE_RANGE_TYPE_VALUE.ADD_ALL_SLIDE_ANIM);
    // 初始化动画风格值
    const [animationStyle, setAnimationStyle] = useState<string>(ADD_ANIMATION_TYPE_VALUE.SIMPLE_AND_FASHIONABLE);
    // 初始化动画类型值
    const [animationType, setAnimationType] = useState<AnimType>(AnimType.FadeIn);
    const animEditInstance = useRef<IAnimationEditorService>();
    useEffect(() => {
        tdwReport();
        getAnimationEditorService()
            ?.promise()
            .then((service) => {
                animEditInstance.current = service;
            });
    }, []);
    // 设置页面范围
    const onChangeAddSlideRange = useCallback((value) => {
        setAddSlideRangeValue(value);
    }, []);

    // 设置动画风格
    const onChangeAnimationStyle = useCallback((animStyle) => {
        setAnimationStyle(animStyle);
    }, []);

    // 设置动画类型
    const onChangeAnimationType = useCallback((animType) => {
        setAnimationType(animType);
    }, []);

    // 添加选中页动画
    const addSelectedPageAnimation = useCallback(() => {
        const selectedSlides: ISlideModel[] = selectedSlideList.length ? selectedSlideList : [currentSlide];
        const slideSdkList = selectedSlides.map(slideModel => slideModel.sdkData);
        if (!(slideSdkList && animEditInstance?.current)) return;
        animEditInstance.current.addSelectedSlidesAnimation(
            slideSdkList as ISlideDataModel[],
            animationStyle,
            animationType,
        );
        const currentPreviewSlideIndex = selectedSlides[selectedSlides.length - 1].listIndex;
        if (currentPreviewSlideIndex === undefined) return;
        const currentPreviewSlideShapeNumber = selectedSlides[selectedSlides.length - 1].children.length;
        previewNewAnimationOnCurrentSlide(currentPreviewSlideIndex, currentPreviewSlideShapeNumber);
    }, [selectedSlideList, currentSlide, animationStyle, animationType]);

    // 点击确定
    const handleOk = useCallback(() => {
        if (!slideDataList) return;
        const addSlideRange = {
            [ADD_SLIDE_RANGE_TYPE_VALUE.ADD_ALL_SLIDE_ANIM]: () => {
                if (!animEditInstance?.current) return;
                animEditInstance?.current.addSelectedSlidesAnimation(slideDataList, animationStyle, animationType);
                const currentPreviewSlideShapeNumber = slideModelList[currentSlideIndex].children.length;
                previewNewAnimationOnCurrentSlide(currentSlideIndex, currentPreviewSlideShapeNumber);
            },
            [ADD_SLIDE_RANGE_TYPE_VALUE.ADD_SELECTED_SLIDE_ANIM]: addSelectedPageAnimation,
        };
        const defaultHandler = () => {};
        (addSlideRange[addSlideRangeValue] ?? defaultHandler)();
        onClose();
    }, [slideDataList, addSlideRangeValue, animationStyle, animationType, addSelectedPageAnimation, onClose]);

    // 批量动画-批量添加动画-tdw上报
    const tdwReport = useCallback(() => {
        const reportInfo = {
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.ANIMATION_BATCH,
            action: TDWActionType.ADD,
            eventcode: EventCodeType.DOC_PPT_ANIMATION_BATCH_ADD,
        };
        tdw({
            ...reportInfo,
        });
    }, []);

    /**
     * 设置当前预览页
     * @param currentPreviewSlideIndex
     * @param currentPreviewSlideShapeNumber
     */
    const previewNewAnimationOnCurrentSlide = useCallback((
        currentPreviewSlideIndex: number,
        currentPreviewSlideShapeNumber: number,
    ) => {
        const animPreviewManager = getEditorAnimPreviewManager();
        const animModelList = slideModelList[currentPreviewSlideIndex]
            .animList[0].data.slice(-currentPreviewSlideShapeNumber);
        animPreviewManager?.previewOneToOneAnims(animModelList);
    }, []);

    return (
        <div>
            <Modal
                maskClosable={true}
                title={BATCH_ANIMATION_TITLE}
                visible={dialogVisible}
                onCancel={onClose}
                onOk={handleOk}
                className={styles['batch-add-animation']}
            >
                <div className={styles['modal-content']}>
                    <p className={styles['add-page-round-text']}>{ADD_SLIDE_RANGE_TITLE}</p>
                    <Radio.Group
                        className={styles['add-animation-round-radio']}
                        onChange={onChangeAddSlideRange}
                        value={addSlideRangeValue}
                    >
                        {ADD_SLIDE_RANGE_TYPE_LIST.map(option => (
                            <Radio key={option.value} value={option.value}>
                                {option.text}
                            </Radio>
                        ))}
                    </Radio.Group>

                    <p className={styles['add-animation-style-text']}>{ADD_ANIMATION_STYLE_TITLE}</p>
                    <Select
                        defaultValue={ADD_ANIMATION_TYPE_VALUE.SIMPLE_AND_FASHIONABLE}
                        onChange={onChangeAnimationStyle}
                        dropdownMatchSelectWidth={false}
                        className={styles['add-animation-round-checkbox']}
                    >
                        {ADD_ANIMATION_TYPE_LIST.map(animStyle => (
                            <Select.Option value={animStyle.value} key={animStyle.value}>
                                {animStyle.text}
                            </Select.Option>
                        ))}
                    </Select>

                    {animationStyle === ADD_ANIMATION_TYPE_VALUE.UNIFIED_STYLE ? (
                        <>
                            <p className={styles['add-animation-type-text']}>{ADD_ANIMATION_TYPE_TITLE}</p>
                            <Select
                                defaultValue={animationType}
                                onChange={onChangeAnimationType}
                                dropdownMatchSelectWidth={false}
                                className={styles['add-animation-round-checkbox']}
                                renderTag={(tag: ITag) => {
                                    const animConfigItem = entrList.find(item => item.id === tag.value);
                                    if (!animConfigItem) return null;
                                    const { icon, label } = animConfigItem;
                                    return (
                                        <div className={styles['anim-style-render-tag']} key={tag.value}>
                                            <img src={icon} />
                                            <span className={styles['anim-style-render-tag-text']}>{label}</span>
                                        </div>
                                    );
                                }}
                            >
                                {entrList.map(animConfig => (
                                    <Select.Option value={animConfig.id} key={animConfig.id}>
                                        <div className={styles['anim-type-select-option']}>
                                            <img src={animConfig.icon} />
                                            <span className={styles['anim-type-select-option-label']}>
                                                {animConfig.label}
                                            </span>
                                        </div>
                                    </Select.Option>
                                ))}
                            </Select>
                        </>
                    ) : null}
                </div>
            </Modal>
        </div>
    );
};
export default BatchAddAnimation;
